import React, { useEffect, useMemo, useState } from "react";

import {
  Avatar,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  IconButton,
  Input,
  Select,
  Text,
  Tooltip,
  VStack,
  useColorModeValue,
  useTheme,
  useToast
} from "@chakra-ui/react";
import { FaEdit, FaQuestionCircle, FaSave, FaTimes } from "react-icons/fa";

import { createMember, updateMember } from "~/api/members";
import { calculateAge } from "~/components/members/MembersCard";
import { Member, useAppStore } from "~/store";

// Helper function to check if objects are equal (deep comparison)
const isEqual = (obj1: unknown, obj2: unknown): boolean => {
  // If either is null/undefined, compare directly
  if (obj1 == null || obj2 == null) {
    return obj1 === obj2;
  }

  // If not objects, compare directly
  if (typeof obj1 !== "object" || typeof obj2 !== "object") {
    return obj1 === obj2;
  }

  // Convert to objects and get all keys
  const keys1 = Object.keys(obj1 as object);
  const keys2 = Object.keys(obj2 as object);

  // If number of keys is different, objects are not equal
  if (keys1.length !== keys2.length) {
    return false;
  }

  // Check each key value
  for (const key of keys1) {
    const val1 = (obj1 as Record<string, unknown>)[key];
    const val2 = (obj2 as Record<string, unknown>)[key];

    // For nested objects, recursively compare
    if (typeof val1 === "object" && typeof val2 === "object") {
      if (!isEqual(val1, val2)) {
        return false;
      }
    } else if (val1 !== val2) {
      // For simple values, directly compare
      // Special case: both empty strings and nullish values are considered equal
      if (
        !(
          (val1 === "" && (val2 === null || val2 === undefined || val2 === "")) ||
          (val2 === "" && (val1 === null || val1 === undefined || val1 === ""))
        )
      ) {
        return false;
      }
    }
  }
  return true;
};

// Helper to normalize member data for comparison
const normalizeMemberForComparison = (member: Member | undefined): Partial<Member> | null => {
  if (!member) return null;

  return {
    name: member.name || "",
    birthdate: member.birthdate || "",
    gender: member.gender || "",
    email: member.email || "",
    profileImageUrl: member.profileImageUrl || ""
  };
};

// Reusable component for required field label with asterisk
const RequiredLabel: React.FC<{ children: React.ReactNode; tooltipText?: string }> = ({ children, tooltipText }) => {
  const requiredColor = useColorModeValue("red.500", "red.300");

  return (
    <Flex align="center">
      <Text as="span" mr={1}>
        {children}
      </Text>
      <Text as="span" color={requiredColor} fontWeight="bold">
        *
      </Text>
      {tooltipText && (
        <Tooltip
          label={tooltipText}
          placement="top"
          hasArrow
          bg="gray.700"
          color="white"
          fontSize="sm"
          px={3}
          py={2}
          borderRadius="md"
        >
          <Box as="span" ml={1} cursor="help">
            <Icon as={FaQuestionCircle} color="gray.400" boxSize={3} />
          </Box>
        </Tooltip>
      )}
    </Flex>
  );
};

// Add a function to validate just the birthdate field
const validateBirthdate = (birthdateValue: string | undefined): string => {
  if (!birthdateValue) {
    return "Birthdate is required";
  }

  // Validate date format and ensure it's not a future date
  const birthDate = new Date(birthdateValue);
  const today = new Date();

  if (isNaN(birthDate.getTime())) {
    return "Invalid date format";
  } else if (birthDate > today) {
    return "Birthdate cannot be in the future";
  } else if (birthDate.getFullYear() < 1850) {
    return "Year cannot be earlier than 1850";
  }

  return ""; // No error
};

interface MemberCardProps {
  onMemberCreated?: (createdMember: Member) => void;
  suppressSuccessToast?: boolean;
  mode?: "userMember" | "otherMembers" | "default";
  isUserMember?: boolean;
  onSkip?: () => void;
  onCancel?: () => void;
}

const MemberCard: React.FC<MemberCardProps> = ({
  onMemberCreated,
  suppressSuccessToast = false,
  mode = "default",
  isUserMember = false,
  onSkip,
  onCancel
}) => {
  // Get the current member and edit mode from global state
  const currentMember = useAppStore((state) => state.currentMember);
  const editMode = useAppStore((state) => state.editMode);
  const setEditMode = useAppStore((state) => state.setEditMode);
  const setCurrentMember = useAppStore((state) => state.setCurrentMember);
  const debugMode = useAppStore((state) => state.debugMode);
  const currentGroup = useAppStore((state) => state.currentGroup);
  const toast = useToast();
  const theme = useTheme();

  // Determine if this is a new member (id === 0) or an existing one
  const isNewMember = currentMember?.id === 0;

  // Local form state for editing
  const [formValues, setFormValues] = useState<Member | undefined>(currentMember);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [originalValues, setOriginalValues] = useState<Record<string, unknown>>({});

  // Check if any form values have changed from the original
  const hasChanges = useMemo(() => {
    if (isNewMember) return true; // Always allow saving for new members

    const normalizedCurrent = normalizeMemberForComparison(currentMember);
    const normalizedForm = normalizeMemberForComparison(formValues);

    return !isEqual(normalizedCurrent, normalizedForm);
  }, [currentMember, formValues, isNewMember]);

  // Color mode values
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.700", "gray.200");
  const labelColor = useColorModeValue("gray.600", "gray.400");
  const inputBg = useColorModeValue("white", "gray.700");
  const inputReadOnlyBg = useColorModeValue("gray.50", "gray.700");
  const disabledButtonBg = useColorModeValue("gray.100", "gray.700");
  const disabledButtonColor = useColorModeValue("gray.400", "gray.500");
  const nameColor = useColorModeValue(theme.colors.brand.sage_green, theme.colors.brand.sage_green);

  // Setup keyboard listener for 'f' key when in debug mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if the key is pressed while any input element is focused
      if (
        debugMode &&
        e.key === "f" &&
        !["INPUT", "TEXTAREA", "SELECT"].includes((document.activeElement?.tagName || "").toUpperCase())
      ) {
        toggleEditMode();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [debugMode, editMode]);

  // Update form values when currentMember changes
  useEffect(() => {
    if (currentMember) {
      // Format birthdate to YYYY-MM-DD for date input field if it exists
      let formattedBirthdate = currentMember.birthdate || "";

      // If birthdate exists, ensure it's in the YYYY-MM-DD format
      if (formattedBirthdate) {
        try {
          const date = new Date(formattedBirthdate);
          if (!isNaN(date.getTime())) {
            // Format to YYYY-MM-DD
            formattedBirthdate = date.toISOString().split("T")[0];
          } else {
            console.warn("Invalid date format received:", formattedBirthdate);
          }
        } catch (error) {
          console.error("Error formatting birthdate:", error);
        }
      }

      const updatedFormValues = {
        ...currentMember,
        email: currentMember.email || "", // Initialize email if it exists
        birthdate: formattedBirthdate // Use the properly formatted birthdate
      };

      setFormValues(updatedFormValues);

      // Fix the type issue by only setting non-null values
      const normalizedValues = normalizeMemberForComparison(updatedFormValues);
      if (normalizedValues !== null) {
        setOriginalValues(normalizedValues);
      }

      // Clear any previous form errors when we set a new member
      setFormErrors({});

      // Log the current member for debugging
      console.log("Current member updated:", currentMember);
    }
  }, [currentMember]);

  // If no member is selected, show a message
  if (!currentMember) {
    return (
      <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" borderRadius="lg" boxShadow="md" maxW="600px">
        <CardBody>
          <Text textAlign="center" color={textColor}>
            No member selected. Please select a member from the list.
          </Text>
        </CardBody>
      </Card>
    );
  }

  // Handle input changes in edit mode
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => (prev ? { ...prev, [name]: value } : undefined));

    // Clear error when field is changed
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle input blur for immediate validation
  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Only validate birthdate for now
    if (name === "birthdate") {
      const error = validateBirthdate(value);
      if (error) {
        setFormErrors((prev) => ({ ...prev, [name]: error }));
      }
    }
  };

  // Validate form before saving
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formValues?.name) {
      errors.name = "Name is required";
    }

    // Use the same validation function for birthdate
    if (formValues?.birthdate) {
      const birthdateError = validateBirthdate(formValues.birthdate);
      if (birthdateError) {
        errors.birthdate = birthdateError;
      }
    } else {
      errors.birthdate = "Birthdate is required";
    }

    if (!formValues?.gender) {
      errors.gender = "Gender selection is required";
    }

    // Email is optional but validate format if provided
    if (formValues?.email && formValues.email.trim()) {
      if (!/\S+@\S+\.\S+/.test(formValues.email)) {
        errors.email = "Email is invalid";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSubmit = async () => {
    // Validate input before submitting
    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    // If no changes were made and we're in edit mode (not creating a new member)
    if (!isNewMember && !hasChanges) {
      setEditMode(false);
      return;
    }

    setIsSubmitting(true);

    try {
      if (isNewMember) {
        // Get userHomeData to access defaultGroup
        const userHomeData = useAppStore.getState().userHomeData;
        // Determine which group ID to use - prefer currentGroup, fallback to userHomeData.defaultGroup
        const groupId = currentGroup?.id || userHomeData?.defaultGroup?.id;

        if (!groupId) {
          throw new Error("No group selected. Please select a group first.");
        }

        // Create new member using API
        const result = await createMember({
          name: formValues?.name || "",
          birthdate: formValues?.birthdate || "",
          gender: formValues?.gender || "M",
          email: formValues?.email || "",
          profileImageUrl: formValues?.profileImageUrl,
          groupId: groupId, // Use the determined group ID
          isUserMember: isUserMember // Pass the flag to indicate if this is the user's own member
        });

        console.log("Create member API response:", result);

        if (result.success && result.member) {
          // Update local state with the new member from the server
          // Make sure we keep all fields, including those that might not be returned by the API
          setCurrentMember({
            ...result.member,
            email: result.member.email || formValues?.email || "",
            birthdate: result.member.birthdate || formValues?.birthdate || "",
            gender: result.member.gender || formValues?.gender || "F",
            profileImageUrl: result.member.profileImageUrl || formValues?.profileImageUrl || ""
          });

          // Only show the toast if suppressSuccessToast is false
          if (!suppressSuccessToast) {
            toast({
              title: "Member created",
              description: `${formValues?.name}'s profile has been created successfully.`,
              status: "success",
              duration: 5000,
              isClosable: true
            });
          }

          // Set to read-only mode
          setEditMode(false);

          if (onMemberCreated) {
            onMemberCreated(result.member);
          }
        } else {
          // Handle duplicate email error
          if (result.error?.includes("member with this email already exists") && result.existingMember) {
            toast({
              title: "Member with this email already exists",
              description: `Email addresses must be unique across the entire system. Would you like to invite ${result.existingMember.name} to this group instead?`,
              status: "warning",
              duration: 10000,
              isClosable: true,
              position: "top"
            });

            // Show invite button separately
            toast({
              title: "Invite User",
              description: "Click here to invite this user (feature coming soon)",
              status: "info",
              duration: 5000,
              isClosable: true,
              position: "bottom"
            });
          } else {
            // Show error message
            toast({
              title: "Error creating member",
              description: result.error || "An error occurred while creating the member",
              status: "error",
              duration: 5000,
              isClosable: true
            });
          }
        }
      } else {
        // Update existing member using API
        const result = await updateMember(currentMember.id, {
          name: formValues?.name || "",
          birthdate: formValues?.birthdate || "",
          gender: formValues?.gender || "M",
          email: formValues?.email || "",
          profileImageUrl: formValues?.profileImageUrl
        });

        console.log("Update member API response:", result);

        if (result.success && result.member) {
          // Update local state with the updated member from the server
          // Important: Use the result.member data directly instead of merging with current member
          setCurrentMember(result.member);

          // Only show the toast if suppressSuccessToast is false
          if (!suppressSuccessToast) {
            toast({
              title: "Member updated",
              description: `${formValues?.name}'s information has been updated successfully.`,
              status: "success",
              duration: 5000,
              isClosable: true
            });
          }

          // Set to read-only mode
          setEditMode(false);

          if (onMemberCreated) {
            onMemberCreated(result.member);
          }
        } else {
          // Handle duplicate email error
          if (result.error?.includes("member with this email already exists") && result.existingMember) {
            toast({
              title: "Member with this email already exists",
              description: `Email addresses must be unique across the entire system. Would you like to invite ${result.existingMember.name} to this group instead?`,
              status: "warning",
              duration: 10000,
              isClosable: true,
              position: "top"
            });

            // Show invite button separately
            toast({
              title: "Invite User",
              description: "Click here to invite this user (feature coming soon)",
              status: "info",
              duration: 5000,
              isClosable: true,
              position: "bottom"
            });
          } else {
            // Show error message
            toast({
              title: "Error updating member",
              description: result.error || "An error occurred while updating the member",
              status: "error",
              duration: 5000,
              isClosable: true
            });
          }
        }
      }
    } catch (error) {
      console.error("Error submitting member form:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    if (editMode) {
      // If leaving edit mode, reset form values to current member
      setFormValues(currentMember);
      setFormErrors({});
    } else {
      // If entering edit mode, initialize form with current values
      setFormValues(currentMember);

      // Fix the type issue by only setting non-null values
      const normalizedValues = normalizeMemberForComparison(currentMember);
      if (normalizedValues !== null) {
        setOriginalValues(normalizedValues);
      }
    }
    setEditMode(!editMode);
  };

  // Handle internal cancel button click
  const handleCancel = () => {
    if (onCancel) {
      // If onCancel prop is provided, use that instead
      onCancel();
    } else {
      // Otherwise, just toggle edit mode
      toggleEditMode();
    }
  };

  // Format date for display in read-only mode
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString();
  };

  // Since both modes should look identical, we'll have a single render method
  // that adjusts the form fields based on editMode
  const renderMemberForm = () => (
    <CardBody position="relative">
      {!editMode && (
        <IconButton
          icon={<FaEdit />}
          aria-label="Edit member"
          size="sm"
          colorScheme="blue"
          variant="ghost"
          onClick={toggleEditMode}
          position="absolute"
          top={2}
          right={2}
          zIndex={1}
        />
      )}

      <Flex direction="column" align="center" mb={6}>
        <Avatar size="xl" name={currentMember.name} src={currentMember.profileImageUrl} mb={4} />
        {!editMode && (
          <Text fontSize="2xl" fontWeight="bold" color={nameColor} textAlign="center" mb={2}>
            {currentMember.name}
          </Text>
        )}
      </Flex>

      <VStack spacing={4} align="stretch">
        <FormControl isInvalid={!!formErrors.name}>
          <FormLabel htmlFor="name" color={labelColor}>
            <RequiredLabel>Name</RequiredLabel>
          </FormLabel>
          <Input
            id="name"
            name="name"
            value={editMode ? formValues?.name || "" : currentMember.name}
            onChange={handleInputChange}
            variant={editMode ? "outline" : "filled"}
            readOnly={!editMode}
            bg={editMode ? inputBg : inputReadOnlyBg}
            border={editMode ? undefined : "none"}
            _readOnly={{ cursor: "default" }}
            _hover={editMode ? undefined : { border: "none" }}
            placeholder="Name"
            color={nameColor}
            fontWeight="bold"
          />
          {!!formErrors.name && <FormErrorMessage>{formErrors.name}</FormErrorMessage>}
        </FormControl>

        <FormControl isInvalid={!!formErrors.email}>
          <FormLabel htmlFor="email" color={labelColor}>
            Email{" "}
            <Text as="span" fontSize="xs" color="gray.500" fontStyle="italic">
              (optional)
            </Text>
          </FormLabel>
          <Input
            id="email"
            name="email"
            type="email"
            value={editMode ? formValues?.email || "" : currentMember.email || ""}
            onChange={handleInputChange}
            variant={editMode ? "outline" : "filled"}
            readOnly={!editMode}
            bg={editMode ? inputBg : inputReadOnlyBg}
            border={editMode ? undefined : "none"}
            _readOnly={{ cursor: "default" }}
            _hover={editMode ? undefined : { border: "none" }}
            placeholder="Email address (optional)"
          />
          {!!formErrors.email && <FormErrorMessage>{formErrors.email}</FormErrorMessage>}
        </FormControl>

        <FormControl isInvalid={!!formErrors.birthdate}>
          <FormLabel htmlFor="birthdate" color={labelColor}>
            <RequiredLabel tooltipText="Age is important for accurate health metrics and analysis. If you don't know the exact date, please estimate based on approximate age.">
              Birthdate{" "}
              {calculateAge(editMode ? formValues?.birthdate : currentMember.birthdate) > 0 &&
                (editMode
                  ? `(${calculateAge(formValues?.birthdate)} years)`
                  : `(${calculateAge(currentMember.birthdate)} years)`)}
            </RequiredLabel>
          </FormLabel>
          {editMode ? (
            <Input
              id="birthdate"
              name="birthdate"
              type="date"
              value={formValues?.birthdate || ""}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              bg={inputBg}
            />
          ) : (
            <Input
              id="birthdate"
              value={formatDate(currentMember.birthdate)}
              readOnly
              variant="filled"
              bg={inputReadOnlyBg}
              border="none"
              _hover={{ border: "none" }}
              _readOnly={{ cursor: "default" }}
            />
          )}
          {!!formErrors.birthdate && <FormErrorMessage>{formErrors.birthdate}</FormErrorMessage>}
        </FormControl>

        <FormControl isInvalid={!!formErrors.gender}>
          <FormLabel htmlFor="gender" color={labelColor}>
            <RequiredLabel tooltipText="Gender is used for health metrics and personalized analysis. Different biological factors affect health recommendations.">
              Gender
            </RequiredLabel>
          </FormLabel>
          {editMode ? (
            <Select id="gender" name="gender" value={formValues?.gender || "M"} onChange={handleInputChange}>
              <option value="M">Male</option>
              <option value="F">Female</option>
              <option value="O">Other</option>
            </Select>
          ) : (
            <Input
              id="gender"
              value={
                currentMember.gender === "M"
                  ? "Male"
                  : currentMember.gender === "F"
                    ? "Female"
                    : currentMember.gender === "O"
                      ? "Other"
                      : ""
              }
              readOnly
              variant="filled"
              bg={inputReadOnlyBg}
              border="none"
              _hover={{ border: "none" }}
              _readOnly={{ cursor: "default" }}
            />
          )}
          {!!formErrors.gender && <FormErrorMessage>{formErrors.gender}</FormErrorMessage>}
        </FormControl>
      </VStack>

      {editMode ? (
        <Flex mt={6} gap={4} justify={mode === "userMember" && onSkip ? "space-between" : "flex-end"}>
          {/* Show Skip button when in userMember mode and onSkip is provided */}
          {mode === "userMember" && onSkip && (
            <Tooltip label="I will not be tracking my own health metrics" placement="top" hasArrow maxW="300px">
              <Button colorScheme="gray" variant="outline" onClick={onSkip} isDisabled={isSubmitting}>
                Skip
              </Button>
            </Tooltip>
          )}

          <Flex gap={4}>
            <Button
              leftIcon={<FaTimes />}
              colorScheme="red"
              variant="outline"
              onClick={handleCancel}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>

            <Tooltip
              label={!hasChanges && !isNewMember ? "No changes to save" : ""}
              isDisabled={hasChanges || isNewMember}
            >
              <Button
                leftIcon={<FaSave />}
                colorScheme={hasChanges || isNewMember ? "green" : "gray"}
                bg={!hasChanges && !isNewMember ? disabledButtonBg : undefined}
                color={!hasChanges && !isNewMember ? disabledButtonColor : undefined}
                _hover={!hasChanges && !isNewMember ? { bg: disabledButtonBg } : undefined}
                onClick={handleSubmit}
                isLoading={isSubmitting}
                loadingText="Saving..."
              >
                {isNewMember ? "Create" : "Save"}
              </Button>
            </Tooltip>
          </Flex>
        </Flex>
      ) : null}

      {debugMode && (
        <Text fontSize="xs" mt={2} textAlign="center" color="gray.500">
          Press 'F' key to toggle edit mode
        </Text>
      )}
    </CardBody>
  );

  return (
    <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" borderRadius="lg" boxShadow="md" maxW="450px">
      {renderMemberForm()}
      {/* 
        Recommendations are now displayed in the main layout in MembersPage.tsx
        SupplementHistory is now displayed separately in the CurrentSupplements component 
      */}
    </Card>
  );
};

export default MemberCard;
